/**
 * Admin · Resend Daily Usage Tile (Phase 2L-A138)
 *
 * Read-only operator visibility on Admin Dashboard. Renders the
 * trailing-14-day Resend volume + estimated spend trajectory using
 * data from `email_logs`. Sits below the A136 failures tile.
 *
 * Pure GET. No mutations. No template / send-path changes.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Mail, Loader2, RefreshCw, AlertTriangle, TrendingUp,
  DollarSign, Sparkles, ChevronDown, ChevronRight, Gauge,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { formatDateAU } from "@/lib/formatDateAU";

const BAR_MAX_HEIGHT_PX = 64;

// Human-friendly labels for the category breakdown expander. Anything
// not listed here falls through to a title-cased version of the raw
// category key, so new categories work without a code change.
const CATEGORY_LABELS = {
  booking_confirmation:                  "Booking confirmations",
  booking_confirmation_admin:            "Booking confirmations · admin",
  booking_pay_link_customer:             "Pay-link · customer",
  booking_pay_link_team:                 "Pay-link · team",
  booking_pay_link_reminder_customer:    "Pay-link reminder · customer",
  booking_pay_link_reminder_team:        "Pay-link reminder · team",
  booking_invoice_customer:              "Invoice · customer",
  booking_invoice_team:                  "Invoice · team",
  booking_invoice_resend:                "Invoice resend",
  customer_report:                       "Customer report",
  manual_customer_email:                 "Manual customer email",
  admin_booking_notification:            "Admin booking notification",
  agency_user_invite:                    "Agency invite",
  agency_user_invite_resend:             "Agency invite resend",
  invite_neighbour:                      "Neighbour invite",
  password_reset:                        "Password reset",
  membership_renewal_reminder:           "Membership renewal reminder",
  cert_share_partner:                    "Certificate share",
  posted_kit_dispatch:                   "Posted kit dispatch",
  customer_assessment_request:           "Customer assessment request",
  customer_proposal_request:             "Customer proposal request",
  customer_reply_alert:                  "Customer reply alert",
  login_email_changed_new:               "Login email change · new",
  login_email_changed_old:               "Login email change · old",
  payout_scheduled:                      "Payout · scheduled",
  payout_paid:                           "Payout · paid",
  xero_upload:                           "Xero upload",
  pm_application_admin_notification:     "PM application · admin",
  pm_application_confirmation:           "PM application · confirmation",
  pm_welcome:                            "PM welcome",
  pm_welcome_resend:                     "PM welcome resend",
  pm_decline:                            "PM decline",
  uncategorised:                         "Uncategorised",
};

function prettyCategory(key) {
  if (CATEGORY_LABELS[key]) return CATEGORY_LABELS[key];
  if (!key) return "Uncategorised";
  return String(key)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function centsToDollars(c) {
  return `$${(Number(c || 0) / 100).toFixed(2)}`;
}

function shortDate(iso) {
  if (!iso) return "—";
  // iso shape "2026-06-30" → "30 Jun"
  const d = new Date(iso + "T00:00:00Z");
  return `${d.getUTCDate()} ${d.toLocaleString("en-AU", { month: "short", timeZone: "UTC" })}`;
}

function resetDateShort(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.getUTCDate()} ${d.toLocaleString("en-AU", { month: "short", timeZone: "UTC" })}`;
}

/**
 * Phase 2L-A138 v2 — Monthly free-tier quota progress bar.
 *
 * Thresholds (matches backend `_threshold_for`):
 *   < 75  → healthy (emerald)
 *   75–89 → watch   (amber)
 *   90–99 → warning (rose)
 *   ≥ 100 → over    (rose striped)
 */
function QuotaProgressBar({ quota, mtd, byCategory }) {
  const [expanded, setExpanded] = useState(false);

  if (!quota) return null;

  const pct = Number(quota.used_pct || 0);
  const threshold = quota.threshold || "healthy";
  const sent = Number(mtd?.sent || 0);
  const limit = Number(quota.monthly_free || 0);
  const remaining = Number(quota.remaining || 0);
  const daysUntilReset = Number(quota.days_until_reset ?? 0);
  const projected = Number(mtd?.projected_month_end || 0);

  // Colour map — kept identical to the backend threshold labels.
  const styles = {
    healthy: { fill: "bg-emerald-500", track: "bg-emerald-100", ring: "border-emerald-200", text: "text-emerald-800", chip: "bg-emerald-50 text-emerald-800 border-emerald-300" },
    watch:   { fill: "bg-amber-500",   track: "bg-amber-100",   ring: "border-amber-200",   text: "text-amber-800",   chip: "bg-amber-50 text-amber-800 border-amber-300" },
    warning: { fill: "bg-rose-500",    track: "bg-rose-100",    ring: "border-rose-200",    text: "text-rose-800",    chip: "bg-rose-50 text-rose-800 border-rose-300" },
    over:    { fill: "bg-rose-600 bg-[repeating-linear-gradient(45deg,rgba(255,255,255,0.25)_0_8px,transparent_8px_16px)]", track: "bg-rose-100", ring: "border-rose-300", text: "text-rose-900", chip: "bg-rose-100 text-rose-900 border-rose-400" },
  };
  const s = styles[threshold] || styles.healthy;

  // Clamp visual bar to 100 even when over quota.
  const barPct = Math.max(0, Math.min(100, pct));

  const callout = {
    watch:   "Approaching monthly free-tier limit. Keep an eye on send-heavy workflows.",
    warning: "Free-tier limit is close. Consider staging non-critical sends.",
    over:    "Free-tier quota exceeded — Resend may pause sends or bill overage depending on your plan.",
  }[threshold];

  return (
    <div
      data-testid="admin-resend-quota-bar"
      className={`rounded-2xl border ${s.ring} bg-white px-4 py-3 mb-4`}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <Gauge className={`w-4 h-4 ${s.text} shrink-0`} />
          <div
            data-testid="admin-resend-quota-label"
            className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-700"
          >
            Monthly free-tier usage
          </div>
          <span
            data-testid="admin-resend-quota-threshold-chip"
            data-threshold={threshold}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${s.chip}`}
          >
            {threshold}
          </span>
        </div>
        <div className="text-[11px] text-slate-500 tabular-nums shrink-0">
          resets {resetDateShort(quota.resets_iso)} · in {daysUntilReset} {daysUntilReset === 1 ? "day" : "days"}
        </div>
      </div>

      <div className="flex items-baseline justify-between mb-1.5">
        <div className="text-base font-display font-bold text-slate-900 tabular-nums">
          <span data-testid="admin-resend-quota-sent">{sent.toLocaleString()}</span>
          <span className="text-slate-400 font-normal"> / {limit.toLocaleString()} emails</span>
        </div>
        <div
          data-testid="admin-resend-quota-pct"
          className={`text-base font-display font-bold tabular-nums ${s.text}`}
        >
          {pct.toFixed(1)}%
        </div>
      </div>

      <div
        className={`w-full h-3 rounded-full ${s.track} overflow-hidden`}
        role="progressbar"
        aria-valuenow={Math.min(100, pct)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          data-testid="admin-resend-quota-bar-fill"
          data-threshold={threshold}
          className={`h-full ${s.fill} transition-[width] duration-500`}
          style={{ width: `${barPct}%` }}
        />
      </div>

      <div className="flex items-center justify-between mt-1.5 text-[11px] text-slate-500 tabular-nums">
        <div>
          {mtd?.avg_per_day || 0} avg/day · projected month-end{" "}
          <span className={projected > limit ? "font-bold text-rose-700" : ""}>
            {projected.toLocaleString()}
          </span>
        </div>
        <div>
          {remaining.toLocaleString()} remaining
        </div>
      </div>

      {callout && (
        <div
          data-testid="admin-resend-quota-warning-callout"
          data-threshold={threshold}
          className={`mt-3 rounded-xl border ${s.ring} bg-white p-3 text-xs ${s.text} flex items-start gap-2`}
        >
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold mb-1">{callout}</div>
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              data-testid="admin-resend-quota-breakdown-toggle"
              className="inline-flex items-center gap-1 underline underline-offset-2 text-[11px] font-medium hover:opacity-80"
            >
              {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              {expanded ? "Hide category breakdown" : "See which categories are burning the quota"}
            </button>
          </div>
        </div>
      )}

      {/* Category breakdown expander — always available; auto-visible in warn/over. */}
      {threshold === "healthy" && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid="admin-resend-quota-breakdown-toggle"
          className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-slate-700 underline underline-offset-2"
        >
          {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          {expanded ? "Hide category breakdown" : "Show category breakdown"}
        </button>
      )}

      {expanded && (
        <div
          data-testid="admin-resend-quota-breakdown"
          className="mt-3 rounded-xl border border-slate-200 bg-slate-50 overflow-hidden"
        >
          {(!byCategory || byCategory.length === 0) ? (
            <div className="p-3 text-[11px] text-slate-500">
              No categorised sends for this month yet.
            </div>
          ) : (
            <table className="w-full text-[12px]">
              <thead className="bg-slate-100 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                <tr>
                  <th className="text-left px-3 py-1.5">Category</th>
                  <th className="text-right px-3 py-1.5">Sent (MTD)</th>
                  <th className="text-right px-3 py-1.5">Share</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {byCategory.map((row) => {
                  const share = sent > 0 ? (row.sent / sent) * 100 : 0;
                  return (
                    <tr key={row.category} data-testid={`admin-resend-quota-cat-${row.category}`}>
                      <td className="px-3 py-1.5 text-slate-800">{prettyCategory(row.category)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-slate-800">{row.sent.toLocaleString()}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{share.toFixed(1)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <div className="px-3 py-1.5 text-[10px] text-slate-500 border-t border-slate-100 bg-slate-50">
            Top {byCategory?.length || 0} categories · month-to-date · sourced from{" "}
            <code className="bg-white px-1 rounded">email_logs</code>.
          </div>
        </div>
      )}
    </div>
  );
}

export function ResendDailyUsageTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/admin/email-health/daily-usage", { params: { days: 14 } });
      setData(res.data);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const series = data?.series || [];
  const totals = data?.totals || { sent: 0, failed: 0, skipped: 0, spend_cents: 0 };
  const last7 = data?.last7 || { sent: 0, spend_cents: 0, avg_per_day: 0 };
  const peak = data?.peak_day;
  const pricing = data?.pricing_model;
  const isEmpty = data?.is_empty;
  const maxSent = Math.max(1, ...series.map((d) => d.sent));
  const quota = data?.quota;
  const mtd = data?.month_to_date;
  const byCategory = data?.by_category_month || [];

  return (
    <div
      data-testid="admin-resend-daily-usage-tile"
      className="rounded-3xl bg-white border border-slate-200 p-6"
    >
      <div className="flex items-start justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-cyan-600" />
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700">
            Resend Daily Usage
          </div>
        </div>
        <button
          type="button"
          data-testid="admin-resend-daily-usage-refresh"
          title="Refresh"
          onClick={load}
          disabled={loading}
          className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-700"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </button>
      </div>

      {error ? (
        <div
          data-testid="admin-resend-daily-usage-error"
          className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>{error}</div>
        </div>
      ) : loading && !data ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : isEmpty ? (
        <>
          {/* Even with no traffic, show the quota bar at 0% so ops can
              still see the free-tier ceiling and reset date. */}
          <QuotaProgressBar quota={quota} mtd={mtd} byCategory={byCategory} />
          <div
            data-testid="admin-resend-daily-usage-empty"
            className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4"
          >
          <Sparkles className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
          <div>
            <div className="font-display font-semibold text-slate-900 text-sm">No Resend traffic yet</div>
            <div className="text-xs text-slate-500 mt-0.5">Once outbound emails start landing in <code className="text-[10px] bg-white px-1 rounded">email_logs</code>, daily volume and estimated spend will appear here.</div>
          </div>
        </div>
        </>
      ) : (
        <>
          {/* Phase 2L-A138 v2 — Monthly free-tier quota progress bar */}
          <QuotaProgressBar quota={quota} mtd={mtd} byCategory={byCategory} />

          {/* Headline tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="admin-resend-usage-headline">
            <UsageTile testid="usage-tile-sent-14d" icon={Mail} label="Sent · 14d" value={totals.sent.toLocaleString()} hint="emails accepted by Resend" />
            <UsageTile testid="usage-tile-spend-14d" icon={DollarSign} label="Est. spend · 14d" value={centsToDollars(totals.spend_cents)} hint="based on Pro tier marginal pricing" />
            <UsageTile testid="usage-tile-sent-7d" icon={TrendingUp} label="Sent · 7d" value={last7.sent.toLocaleString()} hint={`avg ${last7.avg_per_day}/day`} />
            <UsageTile testid="usage-tile-peak-day" icon={TrendingUp} label="Peak day" value={peak ? peak.sent.toLocaleString() : "0"} hint={peak ? `on ${shortDate(peak.date)}` : "—"} />
          </div>

          {/* Bar sparkline */}
          <div
            data-testid="admin-resend-daily-usage-chart"
            className="bg-slate-50 rounded-2xl border border-slate-200 p-4 mb-3"
            role="img"
            aria-label={`Daily Resend send volume, last ${data.days} days`}
          >
            <div className="flex items-end gap-1.5 h-[80px]">
              {series.map((d) => {
                const h = Math.max(2, Math.round((d.sent / maxSent) * BAR_MAX_HEIGHT_PX));
                return (
                  <div
                    key={d.date}
                    className="flex-1 group relative flex flex-col items-center justify-end"
                    data-testid={`usage-bar-${d.date}`}
                  >
                    <div
                      className="w-full rounded-t-sm bg-cyan-500 group-hover:bg-cyan-600 transition-colors"
                      style={{ height: `${h}px` }}
                      title={`${shortDate(d.date)} · ${d.sent} sent · ${centsToDollars(d.spend_cents)}${d.failed ? ` · ${d.failed} failed` : ""}`}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between text-[10px] text-slate-400 mt-2 tabular-nums">
              <span>{shortDate(series[0]?.date)}</span>
              <span>{shortDate(series[Math.floor(series.length / 2)]?.date)}</span>
              <span>{shortDate(series[series.length - 1]?.date)}</span>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto rounded-2xl border border-slate-200">
            <table className="w-full text-sm" data-testid="admin-resend-daily-usage-table">
              <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-right px-3 py-2">Sent</th>
                  <th className="text-right px-3 py-2">Failed</th>
                  <th className="text-right px-3 py-2">Skipped</th>
                  <th className="text-right px-3 py-2">Est. spend</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {[...series].reverse().map((d) => (
                  <tr key={d.date} data-testid={`usage-row-${d.date}`} className="bg-white">
                    <td className="px-3 py-2 text-slate-700 text-[12px] whitespace-nowrap">{shortDate(d.date)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-800">{d.sent.toLocaleString()}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${d.failed > 0 ? "text-amber-700" : "text-slate-400"}`}>{d.failed.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-400">{d.skipped.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-600">{centsToDollars(d.spend_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footnote */}
          <div className="mt-3 text-[11px] text-slate-500">
            Estimated spend uses {pricing
              ? `${(pricing.price_per_email_cents).toFixed(3)}¢ per email (${pricing.price_per_thousand_cents}¢ per 1,000)`
              : "Resend Pro marginal pricing"}.
            {" "}Override via <code className="text-[10px] bg-slate-50 px-1 rounded">RESEND_PRICE_PER_THOUSAND_CENTS</code>.
            Read-only · source: <code className="text-[10px] bg-slate-50 px-1 rounded">email_logs</code>.
          </div>
        </>
      )}
    </div>
  );
}

function UsageTile({ testid, icon: Icon, label, value, hint }) {
  return (
    <div
      data-testid={`admin-resend-${testid}`}
      className="rounded-2xl border border-slate-200 bg-white px-3 py-3"
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-cyan-700">
        <Icon className="w-3 h-3" />
        <span className="truncate">{label}</span>
      </div>
      <div className="text-2xl font-display font-bold leading-tight mt-1 text-slate-900 tabular-nums">
        {value}
      </div>
      {hint && <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}
